import React, { useEffect, useMemo, useState } from "react";
import {
    Box,
    Card,
    Typography,
    TextField,
    Button,
    Stack,
    Avatar,
    Chip,
    Paper,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    InputAdornment,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Switch,
    FormControlLabel
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import DownloadIcon from "@mui/icons-material/Download";
import PeopleIcon from "@mui/icons-material/People";
import SearchIcon from "@mui/icons-material/Search";
import * as XLSX from "xlsx";
import { useAuth } from "../hooks/useAuth";
import AdminLayout from "./AdminLayout";
import { collection, getDocs, doc, updateDoc, getDoc } from "firebase/firestore";
import { db } from "../firebase/config";
import creditService from "../services/creditService";
import { unlockAssessmentForUser, getAssessmentLock } from "../services/assessmentService";

const COLLEGES = [
    "National Institute of Medical Sciences & Research",
    "Nims Institute of Engineering & Technology",
    "Nims School of Business",
    "Nims School of Law",
    "Nims Institute of Hotel Management",
    "Nims Dental College & Hospital",
    "Nims Institute of Pharmacy",
    "Nims School of Nursing",
    "Nims College of Physiotherapy & Occupational Therapy",
    "Nims College of Allied & Health Care Sciences (Paramedical)",
    "Nims Institute of Humanities & Liberal Arts",
    "Nims Institute of Public Health and Governance",
    "Nims Institute of Fashion Technology",
    "Nims Institute of Aviation",
    "Nims School of Forensic Sciences",
    "Marik Institute of Computing, AI, Robotics & Cybernetics",
    "Nims Institute of Travel & Tourism Management"
];

const ROLES = ["student", "startup", "faculty", "admin"];

export default function UsersListPage() {
    const { userProfile } = useAuth();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState("");
    const [roleFilter, setRoleFilter] = useState("");
    const [collegeFilter, setCollegeFilter] = useState("");
    const [creditDialog, setCreditDialog] = useState({ open: false, userId: null, amount: 100 });
    const [locks, setLocks] = useState({});

    useEffect(() => {
        if (userProfile?.role !== "admin") return;
        const fetchUsers = async () => {
            setLoading(true);
            try {
                const snapshot = await getDocs(collection(db, "users"));
                const usersList = snapshot.docs.map((d) => {
                    const data = d.data();
                    const createdAt =
                        data.createdAt && data.createdAt.toDate
                            ? data.createdAt.toDate()
                            : data.createdAt instanceof Date
                                ? data.createdAt
                                : null;
                    return {
                        id: d.id,
                        name: `${data.firstName || ""} ${data.lastName || ""}`.trim() || data.displayName || "",
                        email: data.email || "",
                        mobile: data.phone || data.mobile || "",
                        role: data.role || "student",
                        collegeName: data.collegeName || "",
                        isNimsStudent: data.isNimsStudent || false,
                        createdAt,
                        status: data.status || (data.emailVerified ? "verified" : "pending"),
                        totalCredits: data.totalCredits || 0,
                        dashboardUnlocked: !!data.dashboardUnlocked
                    };
                });
                setUsers(usersList);
                // Fetch assessment locks for visible users
                const lockMap = {};
                for (const u of usersList) {
                    try {
                        const lock = await getAssessmentLock(u.id);
                        lockMap[u.id] = !!lock?.locked;
                    } catch (_) {
                        lockMap[u.id] = false;
                    }
                }
                setLocks(lockMap);
            } catch (error) {
                console.error("Error fetching users:", error);
            } finally {
                setLoading(false);
            }
        };
        fetchUsers();
    }, [userProfile]);

    const handleRoleChange = async (userId, newRole) => {
        if (!userProfile || userProfile.role !== "admin") return;
        try {
            await updateDoc(doc(db, "users", userId), {
                role: newRole,
                updatedAt: new Date()
            });
            setUsers((prev) =>
                prev.map((u) => (u.id === userId ? { ...u, role: newRole } : u))
            );
        } catch (e) {
            console.error("Failed to update role:", e);
        }
    };

    const handleToggleAccess = async (userId, value) => {
        if (!userProfile || userProfile.role !== "admin") return;
        try {
            await updateDoc(doc(db, "users", userId), {
                dashboardUnlocked: value,
                dashboardUnlockedAt: value ? new Date() : null,
                updatedAt: new Date()
            });
            setUsers(prev => prev.map(u => u.id === userId ? { ...u, dashboardUnlocked: value } : u));
        } catch (e) {
            console.error("Failed to toggle access:", e);
        }
    };

    const openCreditDialog = (uid) => setCreditDialog({ open: true, userId: uid, amount: 100 });
    const closeCreditDialog = () => setCreditDialog({ open: false, userId: null, amount: 100 });
    const assignCredits = async () => {
        if (!creditDialog.userId) return;
        try {
            await creditService.assignCredit({
                assignedTo: creditDialog.userId,
                amount: creditDialog.amount,
                activityType: "admin_assigned",
                description: "Admin credit allocation",
                assignedBy: userProfile?.id || userProfile?.uid || "ADMIN"
            });
            const ref = doc(db, "users", creditDialog.userId);
            const snap = await getDoc(ref);
            const total = snap.exists() ? (snap.data().totalCredits || 0) : 0;
            setUsers(prev => prev.map(u => u.id === creditDialog.userId ? { ...u, totalCredits: total } : u));
            closeCreditDialog();
        } catch (e) {
            console.error("Failed to assign credits:", e);
        }
    };

    const handleUnlockAssessment = async (userId) => {
        if (!userProfile || userProfile.role !== "admin") return;
        try {
            await unlockAssessmentForUser(userId, userProfile.id || userProfile.uid || "ADMIN", "manual_admin_unlock");
            setLocks(prev => ({ ...prev, [userId]: false }));
        } catch (e) {
            console.error("Failed to unlock assessment:", e);
        }
    };

    const filteredUsers = useMemo(() => {
        return users.filter((u) => {
            const text = `${u.name} ${u.email} ${u.mobile} ${u.collegeName}`.toLowerCase();
            const matchesSearch = text.includes(search.toLowerCase());
            const matchesRole = roleFilter ? u.role === roleFilter : true;
            const matchesCollege = collegeFilter ? u.collegeName === collegeFilter : true;
            return matchesSearch && matchesRole && matchesCollege;
        });
    }, [users, search, roleFilter, collegeFilter]);

    const exportToExcel = () => {
        const data = filteredUsers.map((u) => ({
            Name: u.name,
            Email: u.email,
            Role: u.role,
            "NIMS Student": u.isNimsStudent ? "Yes" : "No",
            "College Name": u.collegeName || "-",
            "Registration Date": u.createdAt ? u.createdAt.toLocaleDateString() : "",
            Status: u.status || ""
        }));
        const sheet = XLSX.utils.json_to_sheet(data);
        const book = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(book, sheet, "Users");
        XLSX.writeFile(book, `users_${new Date().toISOString().split('T')[0]}.xlsx`);
    };

    if (userProfile?.role !== "admin") {
        return (
            <AdminLayout title="Users">
                <Box sx={{ p: 3 }}>
                    <Typography variant="h6">Unauthorized Access</Typography>
                </Box>
            </AdminLayout>
        );
    }

    const columns = [
        {
            field: "name",
            headerName: "User",
            flex: 1.6,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Avatar sx={{ width: 32, height: 32 }}>
                        {params.value ? params.value.charAt(0).toUpperCase() : "U"}
                    </Avatar>
                    <Box>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {params.value || "-"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {params.row.email}
                        </Typography>
                    </Box>
                </Stack>
            )
        },
        { field: "mobile", headerName: "Mobile No", flex: 0.9 },
        {
            field: "collegeName",
            headerName: "College",
            flex: 1.4,
            renderCell: (params) => (
                <Typography variant="body2">
                    {params.row.isNimsStudent ? params.value || "-" : "Non-NIMS"}
                </Typography>
            )
        },
        {
            field: "totalCredits",
            headerName: "Credits",
            width: 120,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip label={params.value || 0} color="primary" size="small" />
                    <Button size="small" onClick={() => openCreditDialog(params.row.id)}>Add</Button>
                </Stack>
            )
        },
        {
            field: "dashboardUnlocked",
            headerName: "Access",
            width: 150,
            renderCell: (params) => (
                <FormControlLabel
                    control={
                        <Switch
                            checked={!!params.value}
                            onChange={(e) => handleToggleAccess(params.row.id, e.target.checked)}
                        />
                    }
                    label={params.value ? "Granted" : "Locked"}
                />
            )
        },
        {
            field: "role",
            headerName: "Role",
            width: 160,
            renderCell: (params) => (
                <FormControl fullWidth size="small">
                    <Select
                        value={params.row.role}
                        onChange={(e) => handleRoleChange(params.row.id, e.target.value)}
                    >
                        {ROLES.map((r) => (
                            <MenuItem key={r} value={r}>
                                {r.charAt(0).toUpperCase() + r.slice(1)}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            )
        },
        {
            field: "assessment",
            headerName: "Assessment",
            width: 180,
            renderCell: (params) => (
                <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                        label={locks[params.row.id] ? "Locked" : "Unlocked"}
                        color={locks[params.row.id] ? "warning" : "success"}
                        size="small"
                    />
                    <Button size="small" onClick={() => handleUnlockAssessment(params.row.id)}>
                        Unlock
                    </Button>
                </Stack>
            )
        },
        {
            field: "status",
            headerName: "Status",
            width: 130,
            renderCell: (params) => (
                <Chip
                    label={params.value || "pending"}
                    size="small"
                    color={params.value === "verified" ? "success" : "default"}
                />
            )
        }
    ];

    return (
        <AdminLayout title="Users">
            <Box sx={{ p: 3 }}>
                <Box sx={{ mb: 2, display: "flex", alignItems: "center", gap: 1 }}>
                    <PeopleIcon sx={{ color: "primary.main", mr: 1 }} />
                    <Box>
                        <Typography variant="h5" sx={{ fontWeight: 700 }}>
                            Users
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Manage all registered users
                        </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1 }} />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                        variant="outlined"
                        startIcon={<DownloadIcon />}
                        onClick={exportToExcel}
                        disabled={filteredUsers.length === 0}
                    >
                        Download Excel
                    </Button>
                </Box>

                <Card sx={{ p: 2, mb: 2 }}>
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                        <TextField
                            size="small"
                            placeholder="Search by name, email, mobile or college"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            fullWidth
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                        <FormControl size="small" sx={{ minWidth: 160 }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                                value={roleFilter}
                                label="Role"
                                onChange={(e) => setRoleFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {ROLES.map((r) => (
                                    <MenuItem key={r} value={r}>
                                        {r.charAt(0).toUpperCase() + r.slice(1)}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ minWidth: 260 }}>
                            <InputLabel>College</InputLabel>
                            <Select
                                value={collegeFilter}
                                label="College"
                                onChange={(e) => setCollegeFilter(e.target.value)}
                            >
                                <MenuItem value="">All</MenuItem>
                                {COLLEGES.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {c}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Stack>
                </Card>

                <Paper sx={{ height: 500, width: "100%" }}>
                    <DataGrid
                        rows={filteredUsers}
                        columns={columns}
                        loading={loading}
                        disableRowSelectionOnClick
                        pageSizeOptions={[10, 25, 50]}
                        initialState={{
                            pagination: { paginationModel: { pageSize: 10 } }
                        }}
                    />
                </Paper>
            </Box>

            <Dialog open={creditDialog.open} onClose={closeCreditDialog}>
                <DialogTitle>Assign Credits</DialogTitle>
                <DialogContent>
                    <TextField
                        label="Amount"
                        type="number"
                        value={creditDialog.amount}
                        onChange={(e) => setCreditDialog(prev => ({ ...prev, amount: parseInt(e.target.value || 0) }))}
                        fullWidth
                        sx={{ mt: 1 }}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeCreditDialog}>Cancel</Button>
                    <Button variant="contained" onClick={assignCredits}>Assign</Button>
                </DialogActions>
            </Dialog>
        </AdminLayout>
    );
}
